/**
 * Exchange logo icons (full-colour, rendered at 16 px). Files live in
 * ``landing/assets/exchanges/`` and are served by the dashboard's landing
 * static mount at ``/assets/exchanges/*`` — the same absolute URL resolves
 * from both the React dashboard and the SSR ``/token/{slug}`` pages (same
 * origin). Logos sourced from withdrawfees.com (31 CEX marks).
 *
 * Keyed by the canonical exchange key; ``ALIAS`` folds the variants the
 * analyzer emits (``bybit_spot`` → ``bybit``, ``gateio``/``gate_io`` →
 * ``gate``, ``huobi`` → ``htx`` …) onto it. DEX legs (chain shorts like
 * ``eth``/``bsc``) resolve to ``null`` so the caller falls back to text.
 * The DEX/perp venues (hotcoin, hyperliquid, asterdex, lighter, edgex) got
 * CoinGecko-sourced logos 2026-07.
 */

const ICON_BASE = "/assets/exchanges";

// canonical key → shipped filename (mixed .png / .jpg as delivered)
const FILE: Record<string, string> = {
  ascendex: "ascendex.png",
  binance: "binance.jpg",
  bingx: "bingx.png",
  bitfinex: "bitfinex.png",
  bitget: "bitget.jpg",
  bitmart: "bitmart.png",
  bitstamp: "bitstamp.png",
  bybit: "bybit.png",
  cex: "cexio.png",
  coinex: "coinex.jpg",
  coinone: "coinone.png",
  coinstore: "coinstore.png",
  cryptocom: "cryptocom.jpg",
  gate: "gate.png",
  gemini: "gemini.png",
  hashkey: "hashkey.png",
  hitbtc: "hitbtc.png",
  htx: "htx.png",
  kcex: "kcex.png",
  kraken: "kraken.jpg",
  kucoin: "kucoin.png",
  lbank: "lbank.png",
  mexc: "mexc.png",
  okx: "okx.png",
  ourbit: "ourbit.png",
  phemex: "phemex.png",
  pionex: "pionex.png",
  poloniex: "poloniex.png",
  upbit: "upbit.png",
  weex: "weex.png",
  xt: "xt.png",
  // DEX / perp venues — CoinGecko logos (2026-07)
  hotcoin: "hotcoin.png",
  hyperliquid: "hyperliquid.png",
  asterdex: "asterdex.png",
  lighter: "lighter.png",
  edgex: "edgex.png",
};

// analyzer variant → canonical key
const ALIAS: Record<string, string> = {
  bybit_spot: "bybit",
  gateio: "gate",
  gate_io: "gate",
  huobi: "htx",
  krakenfx: "kraken",
  kucoincom: "kucoin",
  coinexcom: "coinex",
  bitgetglobal: "bitget",
  mxc: "mexc",
  cryptodotcom: "cryptocom",
  crypto_com: "cryptocom",
};

/** Resolve an exchange key to a served icon URL, or null when none exists. */
export function getExchangeIconUrl(
  exchange: string | null | undefined,
): string | null {
  if (!exchange) return null;
  const raw = exchange.trim().toLowerCase();
  const key = ALIAS[raw] ?? raw;
  const file = FILE[key];
  return file ? `${ICON_BASE}/${file}` : null;
}
